import { Button } from "@/components/ui";
import { distributionLevels } from "@/lib/utils";
import { setDistributionJobDtl } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetDistributionJobsSimulatePlanned } from "@/services";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const useViewJobDetails = () => {
  const { id, budgetId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const isMasterDataRoute = location.pathname.toLowerCase().includes(routes.masterData);
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const communityName = communityData?.name || communityData?.communityName || "";
  const communityId = (id || communityData?.communityId) as string;
  const budgetListPath = isMasterDataRoute
    ? `/${routes.admin}/${routes.masterData}/${routes.budgetMgmt}`
    : `/${routes.admin}/${routes.adminConsole}/${routes.communities}/${communityId}/${routes.budgetMgmt}`;

  const breadcrumbData = useMemo(() => {
    const currentPage = {
      link: "",
      name: <span className="text-primary">{"Simulate Job"}</span>,
    };
    const budgetCrumb = {
      link: budgetListPath,
      name: "Budget Management",
    };

    if (isMasterDataRoute) {
      return [budgetCrumb, currentPage];
    }

    return [
      {
        link: `/${routes.admin}/${routes.adminConsole}/${routes.communities}`,
        name: `Community - ${communityName || ""}`,
      },
      budgetCrumb,
      currentPage,
    ];
  }, [budgetListPath, communityName, isMasterDataRoute]);

  const title = "Simulate Job";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const {data: simulateDetails, isLoading: isLoadingJobSimulate} = useGetDistributionJobsSimulatePlanned({
    communityId: communityData?.communityId,
    budgetId: budgetId as string,
    queryParams: {
      enabled: !!budgetId
    }
  });


  const columns = useMemo(() => ([
    {
      accessorKey: "glMasterId",
      header: <div>GL</div>,
      cell: ({row}: any) => <Button
        variant="link" 
        className="mx-0 px-0"
        onClick={(e: any) => {
          e.preventDefault();
          e.stopPropagation();
          dispatch(setDistributionJobDtl({simulateDtl: simulateDetails?.data, glMasterId: row.original?.glMasterId, glAccountNumber: row.original?.glAccountNumber, receiverLevel: row?.original?.receiverLevel}))
          navigate(`${row?.original?.glMasterId}`)
        }}>
          {String(row.original?.glAccountNumber || "")}
        </Button>,
    },
    {
      accessorKey: "senderLevel",
      header: <div>Sender</div>,
      accessorFn: (row: any) => String(row?.senderLevel || ""),
    },
    {
      accessorKey: "receiverLevel",
      header: <div>Receiver</div>,
      accessorFn: (row: any) => String(row?.receiverLevel || ""),
    },
    {
      accessorKey: "rule",
      header: <div>Rule</div>,
      accessorFn: (row: any) => distributionLevels[row?.rule] || "",
    },
  ]), [simulateDetails]);

  return {
    columns,
    title,
    breadcrumbData,
    isLoadingJobSimulate,
    simulateDetails: simulateDetails?.data?.glSummary || [],
  }
}

export default useViewJobDetails;